import _ from 'lodash';
import React, {useContext, useMemo} from 'react';
import styled from 'styled-components';
import {withTranslation} from 'react-i18next';

import {FormLabel} from '../../../../ui-toolkit/indexExportV2';

import {getPathDetails, unwrapSingularDataType} from '../../../../ducks/formattersettingshelpers';
import {useLoadedTypes} from '../../../../ducks/typedetail-flattened';
import DetailBlock from '../../../../shared-components/grid-features/detailContent/DetailBlock';
import DetailCategory from '../../../../shared-components/grid-features/detailContent/DetailCategory';
import {DetailGrid} from '../../../../shared-components/grid-features/detailContent/DetailGrid';
import {useFormattersByIds} from '../../../FormattersContext';
import {AutocompleteContext} from '../../../entity-create-edit-shared/Autocomplete';
import {EditorDetail} from '../EditorDetail';
import ToJsonFormatter from '../ToJsonFormatter';
import {FKLink} from './FKLink';
import {fieldStatusIcon} from './fieldStatusIcon';

function groupAttributesByCategory(attributes) {
    let currentCategory = {name: null, fields: []};
    let categories = [currentCategory];
    for (let i = 0; i < attributes.length; i++) {
        let attr = attributes[i];
        if (attr.category && currentCategory.fields.length) {
            currentCategory = {name: attr.category, fields: []};
            categories.push(currentCategory);
        }
        if (attr.category) currentCategory.name = attr.category;
        currentCategory.fields.push(attr);
    }
    return categories;
}

function CardEditor({entityType, formatter, entity, fullEntity, fieldName, fkOptions, t, isRoot}) {
    const unwrappedEntityType = unwrapSingularDataType(entityType);

    const loadedTypes = useLoadedTypes();
    const autocompleteContext = useContext(AutocompleteContext);

    const formatterAttributes = useMemo(() => {
        return _.filter(
            _.get(formatter, 'settings.attributes', []),
            (attr) => getPathDetails(entityType, attr.attributePath).validPath,
        );
    }, [entityType, formatter]);

    const formatterIds = useMemo(() => _.map(formatterAttributes, 'formatterId'), [formatterAttributes]);

    const allFormatters = useFormattersByIds(formatterIds);

    if (!fieldName) {
        fieldName = '';
    } else {
        fieldName = fieldName + '.';
    }

    const categories = groupAttributesByCategory(formatterAttributes);

    return (
        <DetailGrid>
            <DetailBlock isRoot={isRoot}>
                {categories.map((cat) => (
                    <DetailCategory title={cat.name} key={cat.name}>
                        {cat.fields.map((attr) => {
                            const pathDetails = getPathDetails(entityType, attr.attributePath);
                            const {finalAttribute: realAttribute, pathName: attributeName} = pathDetails;

                            const attributeDataType = loadedTypes[realAttribute.dataTypeId];

                            const attributeFormatter = _.find(allFormatters, {id: attr.formatterId});
                            const Edit = _.defaultTo(attributeFormatter, {Formatter: ToJsonFormatter});

                            // when the formatter is readonly we extract the attribute value from
                            // the full entity, because the flattened entity might contain just the id
                            // in which case the formatter would not display the expected value
                            const getDataFromFullEntity = _.get(attributeFormatter, 'readonly', true);
                            const childEntitySource = _.defaultTo(getDataFromFullEntity ? fullEntity : entity, entity);
                            const childEntity = _.get(childEntitySource, attributeName);

                            return (
                                <EditorDetail
                                    direction={Edit.parentDirection}
                                    label={attr.displayName ? attr.displayName + ':' : ''}
                                    icon={fieldStatusIcon(autocompleteContext, fieldName + attributeName)}
                                    key={attr.formatterId + '-' + attr.attributePath}
                                    isCardEditor={Edit.name === 'CardEditor'}
                                    isArrayFormatter={Edit.name === 'ArrayFormatter'}>
                                    {_.has(attr, 'editDescription') && <SubLabel>({attr.editDescription})</SubLabel>}
                                    <Edit.Formatter
                                        entityType={attributeDataType}
                                        formatter={attributeFormatter}
                                        fkOptions={fkOptions}
                                        entity={childEntity}
                                        parentEntity={entity}
                                        fieldName={fieldName + attributeName}
                                        displayName={attr.displayName}
                                        attributePath={attr.attributePath}
                                        parentTypeId={unwrappedEntityType.dataTypeId}
                                    />
                                    <FKLink path={pathDetails} entity={childEntitySource} t={t} />
                                </EditorDetail>
                            );
                        })}
                    </DetailCategory>
                ))}
            </DetailBlock>
        </DetailGrid>
    );
}

const SubLabel = styled(FormLabel)`
    font-style: italic;
`;

export default withTranslation()(CardEditor);
